<script>
  import {
    ArrowRightFromBracketSolid,
    GlobeSolid,
    LifeBuoySolid,
    UserCircleSolid,
    ChartPieSolid,
    PaperClipOutline,
    StarHalfStrokeSolid,
    GridOutline,
  } from 'flowbite-svelte-icons';
  import { LightningIcon, CookieConsent, SideNav, TopNav } from '@button/ui';
  import { createQuery } from '@tanstack/svelte-query';
  import { Banner, Button, Modal } from 'flowbite-svelte';
  import Footer from '../components/Footer.svelte';
  import { getUserDataQuery } from '../data/userData';
  import { NEW_USER } from '../lib/constants';
  import {
    createProfileModalInnerText,
    createProfileModalTitle,
    isShowingLogOutModal,
    openCreateProfileModal,
  } from '../store';
  import { deleteCookie, getCookie } from '../utils';
  import CreateProfileModal from './CreateProfileModal.svelte';
  import { _ } from 'svelte-i18n';
  import UpdateCountryProfileModal from './UpdateCountryProfileModal.svelte';
  import { Link } from 'svelte-routing';
  import { useLocation } from 'svelte-routing';
  import PrimeDayModal from './PrimeDayModal.svelte';

  const location = useLocation();

  $: activeUrl = $location.pathname;

  export let showCreateShortlink = false;
  const userQuery = createQuery(getUserDataQuery);

  $: pages = $userQuery.isSuccess
    ? [
        {
          meta: {
            title: $_('Layout.myLinks', {
              default: 'My Links',
            }),
          },
          icon: GlobeSolid,
          path: '/',
        },
        ...($userQuery.data.object.ccEnabled
          ? [
              {
                meta: {
                  title: 'Creator Connections',
                },
                icon: StarHalfStrokeSolid,
                path: '/creator-connections',
              },
            ]
          : []),
        {
          meta: {
            title: $_('Layout.curatedby', {
              default: 'My CuratedBy',
            }),
          },
          icon: GridOutline,
          path: '/curated-by',
        },
        {
          meta: {
            title: $_('Layout.reports', {
              default: 'Reports',
            }),
          },
          icon: ChartPieSolid,
          path: '/reports',
        },
        {
          meta: {
            title: $_('Layout.profile', {
              default: 'Profile',
            }),
          },
          icon: UserCircleSolid,
          path: '/profile-settings',
        },
        // Only include the bulk links page if the user has the feature enabled
        ...($userQuery.data.object.isShortlinkBulkUploadEnabled
          ? [
              {
                meta: {
                  title: $_('Layout.bulkLinks', {
                    default: 'Bulk Links',
                  }),
                },
                icon: PaperClipOutline,
                path: '/bulk-links',
              },
            ]
          : []),
        {
          meta: {
            title: $_('Layout.geolocation', {
              default: 'Earn Internationally',
            }),
          },
          icon: LightningIcon,
          path: '/geolocation',
        },
        {
          divider: true,
        },
        {
          meta: {
            title: $_('Layout.help', {
              default: 'Help',
            }),
          },
          icon: LifeBuoySolid,
          onClick() {
            window.open('/help', '_blank').focus();
          },
        },
        {
          meta: {
            title: $_('Layout.logOut', {
              default: 'Log Out',
            }),
          },
          icon: ArrowRightFromBracketSolid,
          onClick() {
            isShowingLogOutModal.set(true);
          },
        },
      ]
    : [];

  function onConfirmLogOut() {
    window.location = '/logout';
  }

  $: {
    if ($userQuery.isSuccess) {
      const newUserCookie = getCookie(NEW_USER);

      if (newUserCookie) {
        $createProfileModalInnerText = '';
        $createProfileModalTitle =
          'Welcome! Complete Your Profile to Get Started';
        openCreateProfileModal();
        deleteCookie(NEW_USER);
      } else if (Object.keys($userQuery.data.object.profile).length === 0) {
        $createProfileModalTitle = "We've added Account Profiles.";
        $createProfileModalInnerText =
          'Complete your profile to personalize your PostTap experience.';
        openCreateProfileModal();
      }
    }
  }
</script>

<div class="grid h-full grid-rows-[auto_1fr] md:grid-cols-[250px_1fr]">
  {#if $userQuery.isSuccess && $userQuery.data?.object?.realCreatorId}
    <Banner
      dismissable={false}
      class="flex w-full flex-col items-center bg-yellow-300 text-center font-bold text-black md:col-span-2"
    >
      <div>
        You are pretending as {$userQuery.data?.object?.creatorID}
      </div>
    </Banner>
  {/if}
  <TopNav
    {showCreateShortlink}
    {pages}
    showLanguageSelector
    class="md:col-span-2"
  >
    <div slot="logo" class="flex items-center justify-between gap-x-3">
      <img src="/images/posttap-logo-onyx.svg" alt="PostTap" />
      <img
        src="/images/created-by.svg"
        alt="Created by Button"
        class="hidden sm:block"
      />
    </div>
  </TopNav>
  <SideNav {pages} showLanguageSelector />
  <div id="content" class="overflow-y-auto p-4 pt-2 md:p-12">
    <div class="mx-auto w-fit">
      <p class="w-fit text-xs font-bold md:hidden">
        {$_('quickAccess.title', {
          default: 'Quick Access',
        })}
      </p>
      <div class={`flex items-center justify-center gap-2 py-2 pt-1 md:hidden`}>
        <Link
          to="/"
          class={`flex items-center gap-1 rounded-full border bg-gray-50 px-2 py-1 shadow-sm ${activeUrl === '/' ? 'text-blue-500' : ''}`}
        >
          <GlobeSolid size="xs" />
          <p
            class={`text-xs ${activeUrl === '/' ? 'text-blue-500' : 'text-gray-700'}`}
          >
            {$_('quickAccess.myLinks', {
              default: 'My Links',
            })}
          </p>
        </Link>
        <Link
          to="/creator-connections"
          class={`flex items-center gap-1 rounded-full border bg-gray-50 px-2 py-1 shadow-sm ${activeUrl === '/creator-connections' ? 'text-blue-500' : ''}`}
        >
          <StarHalfStrokeSolid size="xs" />
          <p
            class={`text-xs ${activeUrl === '/creator-connections' ? 'text-blue-500' : 'text-gray-700'}`}
          >
            {$_('quickAccess.creatorConnections', {
              default: 'Creator Connections',
            })}
          </p>
        </Link>
        <Link
          to="/curated-by"
          class={`flex items-center gap-1 rounded-full border bg-gray-50 px-2 py-1 shadow-sm ${activeUrl === '/curated-by' ? 'text-blue-500' : ''}`}
        >
          <GridOutline size="xs" />
          <p
            class={`text-xs ${activeUrl === '/curated-by' ? 'text-blue-500' : 'text-gray-700'}`}
          >
            {$_('quickAccess.curatedBy', {
              default: 'Curated By',
            })}
          </p>
        </Link>
      </div>
    </div>
    <slot />
    <Footer />
  </div>
  <CookieConsent divClass="p-4 md:col-span-2" position="static" />
</div>

<CreateProfileModal />
<UpdateCountryProfileModal />
<PrimeDayModal />

<Modal bind:open={$isShowingLogOutModal} size="xs" autoclose>
  <div class="text-center">
    <h3 class="m-5 text-lg font-normal text-gray-500">
      {$_('components.layout.logout.header', {
        default: 'Are you sure you want to log out?',
      })}
    </h3>
    <Button color="red" class="mr-2" on:click={onConfirmLogOut}>
      {$_('components.layout.logout.confirm', {
        default: "Yes, I'm sure",
      })}
    </Button>
    <Button color="alternative">
      {$_('components.layout.logout.cancel', {
        default: 'Cancel',
      })}
    </Button>
  </div>
</Modal>
